import { useCallback, useEffect, useState } from 'react';
import type { PodcastEpisodeDetail, PodcastEpisodeView, PodcastLibrary } from '@almanac/contracts';
import { useApi } from '../../api/useApi';

export type LibraryState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; library: PodcastLibrary };

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** The caller's podcast library, with a retry that reloads it. */
export function usePodcastLibrary(): { state: LibraryState; retry: () => void } {
  const api = useApi();
  const [state, setState] = useState<LibraryState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      let next: LibraryState;
      try {
        next = { status: 'ready', library: await api.getPodcast() };
      } catch (e: unknown) {
        next = { status: 'error', message: errorMessage(e) };
      }
      if (!cancelled) setState(next);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [api, attempt]);

  return { state, retry };
}

export type DetailState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; detail: PodcastEpisodeDetail };

/**
 * One episode's action items and transcript, fetched when `enabled` first
 * turns true. Not cached across mounts: a re-publish can change the next steps
 * without changing the MP3 URL, so each card reads the current version.
 */
export function useEpisodeDetail(
  episode: PodcastEpisodeView,
  enabled: boolean,
): { state: DetailState; retry: () => void } {
  const api = useApi();
  const [state, setState] = useState<DetailState>({ status: 'idle' });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState({ status: 'loading' });
    async function load() {
      let next: DetailState;
      try {
        next = { status: 'ready', detail: await api.getPodcastEpisode(episode.feed, episode.guid) };
      } catch (e: unknown) {
        next = { status: 'error', message: errorMessage(e) };
      }
      if (!cancelled) setState(next);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [api, enabled, episode.feed, episode.guid, attempt]);

  return { state, retry };
}
